import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  InputNumber,
  Button,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Table,
  Image,
  Space,
  Modal,
  Tag,
  Tooltip,
  Statistic,
  Alert
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined,
  SlidersOutlined,
  TableOutlined,
  BarChartOutlined,
  DashboardOutlined
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text, Paragraph } = Typography;

const Simulation = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [results, setResults] = useState(null);
  const [chartUrl, setChartUrl] = useState("");

  const [inputData, setInputData] = useState({
    title: fileName || "Untitled Analysis",
    seed: 0,
    trials: 10,
    categories: [
      { name: "Category 1", value: 0, frequency: 0 },
      { name: "Category 2", value: 0, frequency: 0 },
      { name: "Category 3", value: 0, frequency: 0 }
    ]
  });

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else if (initialConfig) {
      const rowNames = initialConfig.rowNames || [];
      setInputData({
        title: fileName || "Untitled Analysis",
        seed: 0,
        trials: 10,
        categories: rowNames.length > 0 
          ? rowNames.map(name => ({ name, value: 0, frequency: 0 }))
          : [
              { name: "Category 1", value: 0, frequency: 0 },
              { name: "Category 2", value: 0, frequency: 0 },
              { name: "Category 3", value: 0, frequency: 0 }
            ]
      });
      setResults(null);
      setChartUrl("");
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return message.error("Not authenticated.");

      const res = await axios.get(`/api/retrieve_simulation/${id}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setChartUrl(res.data.chart_url);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to load simulation data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    if (!inputData.trials || inputData.trials <= 0) {
      message.error("Number of trials must be greater than zero.");
      return;
    }

    if (inputData.categories.length < 2) {
      message.error("At least two categories are required.");
      return;
    }

    // Validate that frequency is not all zero
    const totalFreq = inputData.categories.reduce((acc, cat) => acc + (cat.frequency || 0), 0);
    if (totalFreq <= 0) {
      message.error("Total frequency must be greater than zero.");
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return message.error("Not authenticated.");

      const payload = {
        name: fileName || inputData.title || "Untitled Analysis",
        input_data: inputData
      };

      const axiosConfig = {
        headers: { Authorization: `Bearer ${token}` }
      };

      let res;
      if (fileId) {
        res = await axios.put(`/api/update_simulation/${fileId}/`, payload, axiosConfig);
      } else {
        res = await axios.post(`/api/save_simulation/`, payload, axiosConfig);
      }

      setResults(res.data.output_data);
      setChartUrl(res.data.chart_url);
      setIsSaved(true);

      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "simulation",
          inputData: res.data.input_data
        });
      }

      setActiveTab("output");
      message.success(fileId ? "Simulation updated successfully!" : "Simulation saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        message.error(err.response?.data?.error || "Failed to save simulation");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (index, field, value) => {
    const updatedCategories = [...inputData.categories];
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value
    };
    setInputData({
      ...inputData,
      categories: updatedCategories
    });
  };

  const handleAddCategory = () => {
    const updatedCategories = [...inputData.categories];
    updatedCategories.push({
      name: `Category ${updatedCategories.length + 1}`,
      value: 0,
      frequency: 0
    });
    setInputData({
      ...inputData,
      categories: updatedCategories
    });
  };

  const handleRemoveCategory = (index) => {
    if (inputData.categories.length <= 2) {
      message.warning("At least two categories are required.");
      return;
    }
    Modal.confirm({
      title: "Remove Category",
      content: `Are you sure you want to remove "${inputData.categories[index].name}"?`,
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        const updatedCategories = inputData.categories.filter((_, i) => i !== index);
        setInputData({
          ...inputData,
          categories: updatedCategories
        });
      }
    });
  };

  const handleFieldChange = (field, value) => {
    setInputData({
      ...inputData,
      [field]: value
    });
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:image')) return url;
    return `http://127.0.0.1:8000${url}`;
  };

  // Compute validation warnings on the fly
  const totalFreq = inputData.categories.reduce((acc, cat) => acc + (cat.frequency || 0), 0);

  const inputColumns = [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      width: "40%",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => handleCategoryChange(index, "name", e.target.value)}
          placeholder="Enter Category Name"
          style={{ borderRadius: "6px" }}
        />
      )
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      width: "25%",
      render: (val, record, index) => (
        <InputNumber
          value={val}
          onChange={(value) => handleCategoryChange(index, "value", value)}
          style={{ width: "100%", borderRadius: "6px" }}
          placeholder="Value"
        />
      )
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      width: "25%",
      render: (val, record, index) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(value) => handleCategoryChange(index, "frequency", value)}
          style={{ width: "100%", borderRadius: "6px" }}
          placeholder="Frequency"
        />
      )
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      align: "center",
      render: (_, record, index) => (
        <Tooltip title="Remove Category">
          <Button
            type="text"
            danger
            disabled={inputData.categories.length <= 2}
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveCategory(index)}
            style={{ borderRadius: "6px" }}
          />
        </Tooltip>
      )
    }
  ];

  const summaryColumns = [
    {
      title: "Category name",
      dataIndex: "Category",
      key: "Category",
      width: 140,
      render: (text, record) => {
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        if (record.Category === "Total") {
          return <Tag color="blue" style={{ fontWeight: "bold" }}>Total</Tag>;
        }
        if (record.Category === "Random Number 1") {
          return <Tag color="green" style={{ fontWeight: "bold" }}>Overall Run</Tag>;
        }
        return <span style={{ fontWeight: "500", color: "#262626" }}>{text}</span>;
      }
    },
    {
      title: "Value",
      dataIndex: "Value",
      key: "Value",
      align: "right",
      width: 90,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>{Number(val).toFixed(2)}</span>;
      }
    },
    {
      title: "Frequency",
      dataIndex: "Frequency",
      key: "Frequency",
      align: "right",
      width: 100,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>{Number(val).toLocaleString()}</span>;
      }
    },
    {
      title: "Probability",
      dataIndex: "Probability",
      key: "Probability",
      align: "right",
      width: 110,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return (
          <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>
            {typeof val === "number" ? val.toFixed(4) : val}
          </span>
        );
      }
    },
    {
      title: "Cumulative Prob.",
      dataIndex: "Cumulative Probability",
      key: "Cumulative Probability",
      align: "right",
      width: 140,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        if (val === "Expected") return <Tag color="geekblue">Expected</Tag>;
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return (
          <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>
            {typeof val === "number" ? val.toFixed(4) : val}
          </span>
        );
      }
    },
    {
      title: "Value * Prob.",
      dataIndex: "Value * Probability",
      key: "Value * Probability",
      align: "right",
      width: 110,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return <span style={{ fontWeight: isSpecial ? "bold" : "normal", color: isSpecial ? "#0050b3" : "inherit" }}>{Number(val).toFixed(4)}</span>;
      }
    },
    {
      title: "Occurrences",
      dataIndex: "Occurrences",
      key: "Occurrences",
      align: "right",
      width: 110,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>{Number(val).toLocaleString()}</span>;
      }
    },
    {
      title: "Percentage",
      dataIndex: "Percentage",
      key: "Percentage",
      align: "right",
      width: 110,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        if (val === "Average") return <Tag color="cyan">Average</Tag>;
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return (
          <span style={{ fontWeight: isSpecial ? "bold" : "normal" }}>
            {typeof val === "number" ? (val * 100).toFixed(2) + "%" : val}
          </span>
        );
      }
    },
    {
      title: "Occurrences * Value",
      dataIndex: "Occurrences * Value",
      key: "Occurrences * Value",
      align: "right",
      width: 160,
      render: (val, record) => {
        if (val === "" || val === undefined) return "-";
        const isSpecial = record.Category === "Total" || record.Category === "Random Number 1";
        return (
          <span style={{ fontWeight: isSpecial ? "bold" : "normal", color: isSpecial ? "#389e0d" : "inherit" }}>
            {Number(val).toFixed(2)}
          </span>
        );
      }
    }
  ];

  const runsColumns = [
    {
      title: "Run Number",
      dataIndex: "Trial",
      key: "Trial",
      width: "15%",
      align: "center",
      sorter: (a, b) => a.Trial - b.Trial,
      render: (trial) => <Tag color="blue">Run #{trial}</Tag>
    },
    {
      title: "Random Number (r)",
      dataIndex: "Random Number",
      key: "Random Number",
      width: "25%",
      align: "right",
      render: (val) => <Text code>{val?.toFixed(4)}</Text>
    },
    {
      title: "Mapped Category",
      dataIndex: "Category",
      key: "Category",
      width: "35%",
      render: (text) => <span style={{ fontWeight: "500", color: "#141414" }}>{text}</span>
    },
    {
      title: "Simulated Value",
      dataIndex: "Value",
      key: "Value",
      width: "25%",
      align: "right",
      render: (val) => <span style={{ fontWeight: "bold", color: "#2f54eb" }}>{val?.toFixed(2)}</span>
    }
  ];

  // Calculate comparison metrics between Theoretical and Simulated values with flexible casing support
  const expectedValue = results ? (results["Expected Value"] !== undefined ? results["Expected Value"] : results["expected_value"]) : undefined;
  const simulatedAverage = results ? (results["Simulated Average"] !== undefined ? results["Simulated Average"] : results["simulated_average"]) : undefined;
  const titleText = results ? (results.Title || results.title || "Analysis") : "Analysis";

  const expVal = expectedValue !== undefined ? Number(expectedValue) : 0;
  const simAvg = simulatedAverage !== undefined ? Number(simulatedAverage) : 0;
  const difference = Math.abs(simAvg - expVal);
  const percentDiff = expVal > 0 ? (difference / expVal) * 100 : 0;

  const summaryData = results ? (results.Summary || results.summary || []) : [];
  const individualRuns = results ? (results["Individual Runs"] || results["individual_runs"] || []) : [];

  const renderInputTab = () => (
    <Card bordered={false} style={{ background: "transparent", padding: 0 }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card 
            title={<span><SlidersOutlined style={{ marginRight: 8, color: "#1890ff" }} />General Configuration</span>}
            bordered={false} 
            style={{ 
              boxShadow: "0 4px 18px rgba(0,0,0,0.03)", 
              borderRadius: "12px",
              background: "#ffffff"
            }}
          >
            <Row gutter={24}>
              <Col xs={24} md={8}>
                <div style={{ marginBottom: "16px" }}>
                  <Text style={{ fontWeight: 500, color: "#595959" }}>Analysis Name</Text>
                  <Input
                    style={{ marginTop: "8px", borderRadius: "6px", height: "40px" }}
                    value={inputData.title}
                    onChange={(e) => handleFieldChange("title", e.target.value)}
                    placeholder="Enter Simulation Title"
                  />
                </div>
              </Col>
              <Col xs={12} md={8}>
                <div style={{ marginBottom: "16px" }}>
                  <Text style={{ fontWeight: 500, color: "#595959" }}>Number of Trials</Text>
                  <InputNumber
                    style={{ width: "100%", marginTop: "8px", borderRadius: "6px", height: "40px", lineHeight: "40px" }}
                    min={1}
                    value={inputData.trials}
                    onChange={(value) => handleFieldChange("trials", value)}
                    placeholder="e.g. 100"
                  />
                </div>
              </Col>
              <Col xs={12} md={8}>
                <div style={{ marginBottom: "16px" }}>
                  <Text style={{ fontWeight: 500, color: "#595959" }}>Random Number Seed</Text>
                  <Tooltip title="Setting a positive integer guarantees repeatable outputs. 0 means completely random.">
                    <InputNumber
                      style={{ width: "100%", marginTop: "8px", borderRadius: "6px", height: "40px", lineHeight: "40px" }}
                      min={0}
                      value={inputData.seed}
                      onChange={(value) => handleFieldChange("seed", value)}
                      placeholder="0 for random"
                    />
                  </Tooltip>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card 
            title={<span><TableOutlined style={{ marginRight: 8, color: "#1890ff" }} />Category Value & Frequency Distribution</span>}
            bordered={false} 
            style={{ 
              boxShadow: "0 4px 18px rgba(0,0,0,0.03)", 
              borderRadius: "12px",
              background: "#ffffff"
            }}
            extra={
              <Button 
                type="primary" 
                ghost
                onClick={handleAddCategory} 
                icon={<PlusOutlined />}
                style={{ borderRadius: "6px" }}
              >
                Add Category
              </Button>
            }
          >
            {totalFreq === 0 && (
              <Alert
                message="Frequency warning"
                description="Make sure to fill frequencies greater than 0 so the calculation can execute."
                type="warning"
                showIcon
                style={{ marginBottom: "16px", borderRadius: "8px" }}
              />
            )}
            <Table
              dataSource={inputData.categories}
              columns={inputColumns}
              pagination={false}
              rowKey={(record, index) => index}
              bordered
              style={{ borderRadius: "8px", overflow: "hidden" }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <div style={{ textAlign: "right", marginTop: "8px" }}>
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              loading={isLoading}
              onClick={saveData}
              style={{
                borderRadius: "8px",
                height: "48px",
                padding: "0 32px",
                fontSize: "16px",
                fontWeight: "600",
                background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
                border: "none",
                boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
                transition: "all 0.3s"
              }}
              className="solve-btn"
            >
              {isSaved ? "Calculate & Update" : "Calculate & Save"}
            </Button>
          </div>
        </Col>
      </Row>
    </Card>
  );

  const renderResultsTab = () => {
    if (!results) {
      return (
        <Card bordered={false} style={{ textAlign: "center", padding: "60px 20px", borderRadius: "12px", boxShadow: "0 4px 18px rgba(0,0,0,0.03)" }}>
          <DashboardOutlined style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }} />
          <p style={{ color: "#8c8c8c", fontSize: "16px" }}>No simulation results available. Fill the inputs and click "Calculate & Save" first.</p>
        </Card>
      );
    }

    return (
      <Card bordered={false} style={{ background: "transparent", padding: 0 }}>
        <style>{`
          .special-row-total {
            background-color: #f5f6ff !important;
            border-top: 2px solid #adc6ff;
            border-bottom: 2px solid #adc6ff;
          }
          .special-row-average {
            background-color: #f6ffed !important;
            border-bottom: 2px solid #b7eb8f;
          }
          .kpi-card {
            transition: transform 0.2s, box-shadow 0.2s;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid rgba(0,0,0,0.03);
          }
          .kpi-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
          }
        `}</style>

        {/* KPI Dashboard */}
        <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} lg={8}>
            <Card
              className="kpi-card"
              bordered={false}
              style={{
                boxShadow: "0 4px 16px rgba(0,0,0,0.02)",
                background: "linear-gradient(135deg, #f5f3ff 0%, #e0e7ff 100%)",
                padding: "8px"
              }}
            >
              <Statistic
                title={<span style={{ color: "#4f46e5", fontWeight: 500 }}>Expected Value (Theoretical)</span>}
                value={expVal}
                precision={4}
                valueStyle={{ color: "#312e81", fontWeight: "bold", fontSize: "28px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card
              className="kpi-card"
              bordered={false}
              style={{
                boxShadow: "0 4px 16px rgba(0,0,0,0.02)",
                background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                padding: "8px"
              }}
            >
              <Statistic
                title={<span style={{ color: "#059669", fontWeight: 500 }}>Simulated Average</span>}
                value={simAvg}
                precision={4}
                valueStyle={{ color: "#064e3b", fontWeight: "bold", fontSize: "28px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={8}>
            <Card
              className="kpi-card"
              bordered={false}
              style={{
                boxShadow: "0 4px 16px rgba(0,0,0,0.02)",
                background: percentDiff <= 5 
                  ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" 
                  : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                padding: "8px"
              }}
            >
              <Statistic
                title={<span style={{ color: percentDiff <= 5 ? "#166534" : "#92400e", fontWeight: 500 }}>Margin of Deviation</span>}
                value={percentDiff}
                precision={2}
                suffix="%"
                valueStyle={{ color: percentDiff <= 5 ? "#14532d" : "#78350f", fontWeight: "bold", fontSize: "28px" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Informative summary banner */}
        <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
          <Col span={24}>
            <Alert
              message={
                <strong>
                  Simulation Summary for {titleText}
                </strong>
              }
              description={`The theoretical expected value is ${expVal.toFixed(4)}, while running ${inputData.trials} trials generated an actual average of ${simAvg.toFixed(4)}. This represents a deviation of ${percentDiff.toFixed(2)}%.`}
              type={percentDiff <= 5 ? "success" : "info"}
              showIcon
              style={{ borderRadius: "8px" }}
            />
          </Col>
        </Row>

        {/* Detailed Results & Chart */}
        <Row gutter={[24, 24]}>
          {/* Summary Table */}
          <Col span={24}>
            <Card 
              title={<span><TableOutlined style={{ marginRight: 8, color: "#1890ff" }} />Simulation Results Summary</span>} 
              bordered={false}
              style={{ 
                boxShadow: "0 4px 18px rgba(0,0,0,0.03)", 
                borderRadius: "12px"
              }}
            >
              <Table
                dataSource={summaryData}
                columns={summaryColumns}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 1040 }}
                rowKey="Category"
                rowClassName={(record) => {
                  if (record.Category === "Total") return "special-row-total";
                  if (record.Category === "Random Number 1") return "special-row-average";
                  return "";
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </Card>
          </Col>

          {/* Individual Trials */}
          <Col span={24}>
            <Card 
              title={<span><InfoCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />Individual Simulation Runs (Trials Detail)</span>} 
              bordered={false}
              style={{ 
                boxShadow: "0 4px 18px rgba(0,0,0,0.03)", 
                borderRadius: "12px"
              }}
            >
              <Table
                dataSource={individualRuns}
                columns={runsColumns}
                pagination={{ 
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} trials`
                }}
                bordered
                size="middle"
                scroll={{ x: "max-content" }}
                rowKey="Trial"
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </Card>
          </Col>

          {/* Probability Distribution Chart */}
          <Col span={24}>
            <Card 
              title={<span><BarChartOutlined style={{ marginRight: 8, color: "#1890ff" }} />Probability Distribution Chart</span>} 
              bordered={false}
              style={{ 
                boxShadow: "0 4px 18px rgba(0,0,0,0.03)", 
                borderRadius: "12px",
                textAlign: "center"
              }}
            >
              {chartUrl ? (
                <div style={{ padding: "10px", background: "#fafafa", borderRadius: "8px", display: "inline-block", boxShadow: "inset 0 0 8px rgba(0,0,0,0.03)" }}>
                  <Image
                    src={getImageUrl(chartUrl)}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                    alt="Simulation Chart"
                    style={{ maxWidth: "100%", maxHeight: "380px", objectFit: "contain", borderRadius: "6px" }}
                  />
                </div>
              ) : (
                <div style={{ padding: "60px 0", color: "#bfbfbf" }}>No image generated</div>
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1f1f1f" }}>
              {inputData.title || "Simulation Analysis"}
            </Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>POM-QM Monte Carlo Demand Simulation</Text>
          </div>
          <div>
            <Space>
              {fileId ? (
                <Tag color="blue" style={{ fontSize: "13px", padding: "4px 8px", borderRadius: "4px" }}>
                  Saved File #{fileId}
                </Tag>
              ) : (
                <Tag color="orange" style={{ fontSize: "13px", padding: "4px 8px", borderRadius: "4px" }}>
                  New Draft Analysis
                </Tag>
              )}
            </Space>
          </div>
        </div>
      }
      bordered={false}
      style={{
        boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
        borderRadius: "16px",
        background: "#f8fafc",
        border: "1px solid rgba(0,0,0,0.02)"
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginBottom: "16px" }}
        className="custom-tabs"
        items={[
          {
            key: "input",
            label: (
              <span>
                <SlidersOutlined style={{ marginRight: 6 }} />
                Inputs
              </span>
            ),
            children: renderInputTab()
          },
          {
            key: "output",
            label: (
              <span>
                <BarChartOutlined style={{ marginRight: 6 }} />
                Results
              </span>
            ),
            children: renderResultsTab()
          }
        ]}
      />
    </Card>
  );
};

export default Simulation;
